import { zonoApi } from '../api';
import {
  TurnoverInvoiceDetailsRequest,
  TurnoverInvoiceDetailsResponse,
  TurnoverInvoicesBuyersRequest,
  TurnoverInvoicesBuyersResponse,
  TurnoverInvoicesContributionRequest,
  TurnoverInvoicesContributionResponse,
  TurnoverInvoicesListRequest,
  TurnoverInvoicesListResponse,
} from './turnover-invoices-model';

//  1. $v1  — always the turnoverinvoice_bool_exp where clause.
//            Used for BOTH the list query and the aggregate so they
//            can never drift (single variable, single source of truth).
//  2. $v2  — order_by  (always [{ created_at: "desc" }], set in the API layer)
//  3. $v3  — limit     (page size, set by the hook / paginator)
//  4. $v4  — offset    (derived from pageNo * pageSize, set by the hook)
//  5. $v5  — inner where clause for a nested relation (contribution lines),
//            always a static constant set in the API layer.

const TURNOVER_INVOICES_LIST_QUERY = `
  query (
    $v1: turnoverinvoice_bool_exp,
    $v2: [turnoverinvoice_order_by!],
    $v3: Int,
    $v4: Int
  ) {
    turnoverinvoice(where: $v1, order_by: $v2, limit: $v3, offset: $v4) {
      id
      invoice_number
      invoice_amount
      invoice_date
      submittedby
      status
      validationStatus
      invoice_validation_status {
        validation_status
      }
      turnoverinvoice_detail {
        buyerdetails
        distributordetails
        matched_buyer_name
        matched_distributor_name
      }
      buyername
      sellername
      created_at
      taxableValue
      gst_percentage
      gst_amount
      discount
      secondary_discount
      cumulative_discount
      discount_amount
      cess
      approved_rejected_by
      remark
      turnoverinvoice_lines_aggregate {
        aggregate {
          count
        }
      }
    }
    turnoverinvoice_aggregate(where: $v1) {
      aggregate {
        count
      }
    }
  }
`;

const TURNOVER_INVOICES_CONTRIBUTION_QUERY = `
  query (
    $v1: turnoverinvoice_bool_exp,
    $v2: [turnoverinvoice_order_by!],
    $v3: Int,
    $v4: Int,
    $v5: turnoverinvoice_line_bool_exp
  ) {
    turnoverinvoice(where: $v1, order_by: $v2, limit: $v3, offset: $v4) {
      id
      turnoverinvoice_lines_aggregate(where: $v5) {
        aggregate {
          count
          sum {
            contribution
            total_amount
            total_amount_with_tax
          }
        }
      }
    }
  }
`;

// Buyers + distributors share the same query shape — the hook calls this
// endpoint twice with different instances; deduplication happens in JS.
const TURNOVER_INVOICES_OPTIONS_QUERY = `
  query (
    $v1: turnoverinvoice_bool_exp,
    $v2: [turnoverinvoice_order_by!],
    $v3: Int,
    $v4: Int
  ) {
    turnoverinvoice(where: $v1, order_by: $v2, limit: $v3, offset: $v4) {
      sellername
      buyername
      turnoverinvoice_detail {
        matched_buyer_name
        matched_distributor_name
        distributordetails
      }
    }
  }
`;

const TURNOVER_INVOICE_DETAILS_QUERY = `
  query ($v1: turnoverinvoice_bool_exp!, $v2: turnoverinvoice_details_bool_exp!) {
    turnoverinvoice(where: $v1, limit: 1) {
      id
      invoice_number
      invoice_amount
      invoice_date
      submittedby
      status
      validationStatus
      invoice_validation_status {
        validation_status
      }
      buyername
      sellername
      created_at
      invoice_file
      cfa_id
      customerId
      turnoverinvoice_detail {
        buyerdetails
        distributordetails
        matched_buyer_details
        matched_distributor_details
      }
      mapped_products_aggregate: turnoverinvoice_lines_aggregate(
        where: { status: { _eq: "MAPPED" } }
      ) {
        aggregate {
          sum {
            total_amount
            total_amount_with_tax
          }
        }
      }
      contribution_lines_aggregate: turnoverinvoice_lines_aggregate(
        where: { status: { _eq: "MAPPED" }, scheme_id: { _is_null: false } }
      ) {
        aggregate {
          sum {
            contribution
            total_amount
            total_amount_with_tax
          }
        }
      }
      turnoverinvoice_lines(order_by: { serial_no: asc }) {
        id
        serial_no
        product_name
        mapped_product_name
        sku_code
        hsn_code
        unit_price
        quantity
        discount
        total_amount
        contribution
        status
        scheme_id
        total_amount_with_tax
        turnover_scheme {
          name
          code
        }
      }
      turnoverinvoice_lines_aggregate {
        aggregate {
          count
        }
      }
    }
    turnoverinvoice_details(where: $v2, limit: 1) {
      id
      invoice_id
      buyerdetails
      distributordetails
      company_name
    }
  }
`;

const ORDER_BY = [{ created_at: 'desc' }];

const CONTRIBUTION_LINES_WHERE = {
  scheme_id: { _is_null: false },
  status: { _eq: 'MAPPED' },
};

export const turnoverInvoicesApi = zonoApi.injectEndpoints({
  endpoints: (builder) => ({
    getTurnoverInvoicesList: builder.query<
      TurnoverInvoicesListResponse,
      TurnoverInvoicesListRequest
    >({
      query: (arg) => ({
        url: 'hub/proxy/api/turnover-schemes/graphql',
        method: 'POST',
        params: { sellerWorkspaceId: arg.sellerWorkspaceId },
        body: {
          query: TURNOVER_INVOICES_LIST_QUERY,
          variables: {
            v1: arg.where, // used for BOTH list and aggregate in the GQL string
            v2: ORDER_BY,
            v3: arg.limit,
            v4: arg.offset,
          },
        },
      }),
      transformResponse: (response: { data: TurnoverInvoicesListResponse }) =>
        response.data,
    }),

    getTurnoverInvoicesContribution: builder.query<
      TurnoverInvoicesContributionResponse,
      TurnoverInvoicesContributionRequest
    >({
      query: (arg) => ({
        url: 'hub/proxy/api/turnover-schemes/graphql',
        method: 'POST',
        params: { sellerWorkspaceId: arg.sellerWorkspaceId },
        body: {
          query: TURNOVER_INVOICES_CONTRIBUTION_QUERY,
          variables: {
            v1: arg.where,
            v2: ORDER_BY,
            v3: arg.limit,
            v4: arg.offset,
            v5: CONTRIBUTION_LINES_WHERE,
          },
        },
      }),
      transformResponse: (response: {
        data: TurnoverInvoicesContributionResponse;
      }) => response.data,
    }),

    getTurnoverInvoiceDetails: builder.query<
      TurnoverInvoiceDetailsResponse,
      TurnoverInvoiceDetailsRequest
    >({
      query: (arg) => ({
        url: 'hub/proxy/api/turnover-schemes/graphql',
        method: 'POST',
        params: { sellerWorkspaceId: arg.sellerWorkspaceId },
        body: {
          query: TURNOVER_INVOICE_DETAILS_QUERY,
          variables: {
            v1: { id: { _eq: arg.invoiceId } },
            v2: { invoice_id: { _eq: arg.invoiceId } },
          },
        },
      }),
      transformResponse: (response: { data: TurnoverInvoiceDetailsResponse }) =>
        response.data,
    }),
    getTurnoverInvoicesBuyers: builder.query<
      TurnoverInvoicesBuyersResponse,
      TurnoverInvoicesBuyersRequest
    >({
      query: (arg) => ({
        url: 'hub/proxy/api/turnover-schemes/graphql',
        method: 'POST',
        params: { sellerWorkspaceId: arg.sellerWorkspaceId },
        body: {
          query: TURNOVER_INVOICES_OPTIONS_QUERY,
          variables: {
            v1: arg.where,
            v2: ORDER_BY,
            v3: arg.limit,
            v4: arg.offset,
          },
        },
      }),
      transformResponse: (response: { data: TurnoverInvoicesBuyersResponse }) =>
        response.data,
    }),
  }),
});

export const {
  useLazyGetTurnoverInvoicesListQuery,
  useLazyGetTurnoverInvoicesContributionQuery,
  useLazyGetTurnoverInvoiceDetailsQuery,
  useLazyGetTurnoverInvoicesBuyersQuery,
} = turnoverInvoicesApi;
